import React from "react";
import { ExternalLink } from "components/ExternalLink";
import { CopyableCodeLine } from "onboarding/components/NumberedStep";
import { track } from "utils/track";
import {
  APPOPTICS_INSTALLER_URL_BASE,
  KB_URL_BASE,
} from "onboarding/constants";
import { KB_APM_URL } from "constants/externalUrls";
const DOTNET_CONFIGURE = `${KB_APM_URL}dotnet/configure`;
const INSTALLER = "DotNetAgent_Setup.exe";
const INSTALLER_URL = `${APPOPTICS_INSTALLER_URL_BASE}/dotnet/${INSTALLER}`;
const KNOWLEDGEBASE_URL = `${KB_URL_BASE}/apm_tracing/dotnet/install/`;
const INSTALLER_FOR_LINUX = "dotnet-agent-package.tgz";
const INSTALLER_URL_LINUX = `${APPOPTICS_INSTALLER_URL_BASE}/dotnet/${INSTALLER_FOR_LINUX}`;
const CORECLR_PROFILER = "{824293AD-22E2-4DAA-BC28-166C140543BE}";
const HOME = "{HOME}";
const APPOPTICS_HOME_APM_DOTNET = "{APPOPTICS_HOME_APM_DOTNET}";

const dotnetSDKInsctructions = props => {
  const { agentRecorderToken, serviceName, selectedOperatingSystem } = props;
  switch (selectedOperatingSystem) {
    case "windows":
      return [
        {
          precedingContent: (
            <div>
              The installer we provide will manage downloading and configuring
              the agent. If you’d prefer to do this manually or need more help,{" "}
              <ExternalLink href={KNOWLEDGEBASE_URL}>
                check out our documentation
              </ExternalLink>.
            </div>
          ),
          title: "Download the .NET agent installer:",
          content: (
            <p onClick={_ => track("Clicked .Net APM installer URL")}>
              <a href={INSTALLER_URL}>{INSTALLER_URL}</a>
            </p>
          ),
        },
        {
          title: "Stop IIS by running via the command line:",
          content: (
            <div onClick={_ => track("Copied the .Net APM stop script")}>
              <CopyableCodeLine> iisreset /STOP </CopyableCodeLine>
            </div>
          ),
        },
        {
          title:
            "Run the installer and provide this service key when requested:",
          content: (
            <div onClick={_ => track("Copied the .Net APM service key")}>
              <CopyableCodeLine>
                {agentRecorderToken}:{serviceName}
              </CopyableCodeLine>
            </div>
          ),
        },
        {
          title: "Restart all application pools:",
          content: (
            <div onClick={_ => track("Copied the .Net APM restart script")}>
              <CopyableCodeLine>iisreset /restart</CopyableCodeLine>
              <p>In a few seconds, the agent will be connected to AppOptics.</p>
            </div>
          ),
        },
      ];
    case "linux":
      return [
        {
          title: "Download and extract the .NET agent installer:",
          content: (
            <React.Fragment>
              <p onClick={_ => track("Clicked .Net APM installer URL")}>
                <a href={INSTALLER_URL_LINUX}>{INSTALLER_URL_LINUX}</a>
              </p>
              <div onClick={_ => track("Copied the .Net APM install script")}>
                <CopyableCodeLine>
                  mkdir -p ${HOME}/appoptics{"\n"}
                  pushd ${HOME}/appoptics{"\n"}
                  curl -sSO
                  https://files.appoptics.com/dotnet/dotnet-agent-package.tgz{
                    "\n"
                  }
                  tar -xvf dotnet-agent-package.tgz{"\n"}
                  popd
                </CopyableCodeLine>
              </div>
            </React.Fragment>
          ),
        },
        {
          title:
            "Designate the agent as a CLR profiler by setting the following environment variables:",
          content: (
            <div
              onClick={_ =>
                track("Copied the environment variables definition")}
            >
              <CopyableCodeLine>
                export APPOPTICS_HOME_APM_DOTNET=${HOME}/appoptics{"\n"}
                export CORECLR_PROFILER={CORECLR_PROFILER}
                {"\n"}
                export CORECLR_PROFILER_PATH=${
                  APPOPTICS_HOME_APM_DOTNET
                }/AppOpticsProfiler.so
              </CopyableCodeLine>
            </div>
          ),
        },
        {
          title:
            "To enable the agent for a process, set the following environment variable for the process:",
          content: (
            <div
              onClick={_ => track("Copied the process environment variable")}
            >
              <CopyableCodeLine>
                export CORECLR_ENABLE_PROFILING=1
              </CopyableCodeLine>
              <div>
                If the above is set at the system level, you may selectively
                disable a process from instrumentation by setting
                CORECLR_ENABLE_PROFILING=0 for the process. For example, to
                exempt the dotnet SDK commands in a bash shell:
                <CopyableCodeLine>
                  CORECLR_ENABLE_PROFILING=0 dotnet --info {"\n"}
                  CORECLR_ENABLE_PROFILING=0 dotnet publish ... {"\n"}
                </CopyableCodeLine>
              </div>
            </div>
          ),
        },
        {
          title:
            "The agent also requires a service key, which can be set either in the configuration file ${APPOPTICS_HOME_APM_DOTNET}/agent.config or as the APPOPTICS_SERVICE_KEY environment variable, for example:",
          content: (
            <div onClick={_ => track("Copied the .Net APM restart script")}>
              <CopyableCodeLine>
                export APPOPTICS_SERVICE_KEY={agentRecorderToken}:{serviceName}
              </CopyableCodeLine>
            </div>
          ),
        },
        {
          title: (
            <div>
              Restart the .NET Core application for these changes to take
              effect. In a few seconds the agent will be connected to AppOptics.
              See{" "}
              <ExternalLink href={DOTNET_CONFIGURE}>
                our configuration documentation
              </ExternalLink>{" "}
              for more information.
            </div>
          ),
        },
      ];
    default:
      return null;
  }
};

export default dotnetSDKInsctructions;
